import React, { useRef, useState } from "react";
import { Checkbox, Color, Flex, Icon } from "src/elements";
import { textVariants } from "src/elements/Text/store/variants";
import { useDebounceEffect } from "src/hooks/useDebounceEffect";
import { useHoverClickable } from "src/utils/hoverClickable";
import { styledSafe } from "src/utils/styledComponent";

const EditableInput = styledSafe("input")`
  margin: 0;
  padding: 0;
  border: 0;
  height: min-content;
  min-height: 20px;
  font-size: ${textVariants.small2.fontSize};
  border-radius: 3px;
  outline: none;
  background: none;
`;

type OnSaveFn<T> = (updatedValue: T) => Promise<T> | T;

interface Props<T = string | number | boolean | null> {
  value: T;
  onSave: OnSaveFn<T>;
  isNumber?: boolean;
  isNullable?: boolean;
  isBoolean?: boolean;
  debounceMs?: number;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
}

export const EditableField = <T = string | number | boolean | null,>({
  value: initialValue,
  onSave,
  isNumber,
  isNullable,
  isBoolean,
  debounceMs = 600,
  inputProps,
}: Props<T>) => {
  const initialString = initialValue === null && isNullable ? "NULL" : String(initialValue ?? "");

  const [value, _setValue] = useState<string>(initialString);
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);

  const refInput = useRef<HTMLInputElement | null>(null);
  const refFirstRun = useRef(true);
  const { isFocused } = useHoverClickable(refInput);

  useDebounceEffect(
    () => {
      saveValue(value);
    },
    [value],
    debounceMs,
  );

  useDebounceEffect(
    () => {
      if (isSaved) setIsSaved(false);
    },
    [isSaved],
    1000,
  );

  const saveValue = async (newValue: string) => {
    if (refFirstRun.current === true) return;

    setIsSaving(true);

    const updatedValue =
      isNullable && newValue === "NULL"
        ? null
        : isNumber
          ? Number(newValue)
          : isBoolean
            ? newValue === "true"
            : newValue;

    try {
      const res = await onSave(updatedValue as T);
      if (res !== false) {
        setIsSaved(true);
      }
    } catch {
      // swallow; caller may show toast
    } finally {
      setIsSaving(false);
      refFirstRun.current = true;
    }
  };

  const setValue = (newValue: string) => {
    refFirstRun.current = false;
    _setValue(newValue);
  };

  return (
    <Flex
      align="center"
      gap="5px"
      padding="0 2px"
      background={isFocused && Color.greenSmoke}
      borderRadius="3px"
      maxHeight="min-content"
    >
      {isBoolean ? (
        <Checkbox
          isChecked={value === "true"}
          setIsChecked={(checked: boolean) => setValue(String(checked))}
        />
      ) : (
        <EditableInput
          ref={refInput}
          value={value}
          onChange={(event) => setValue(event.target.value)}
          autoCorrect="off"
          autoComplete="off"
          {...inputProps}
        />
      )}

      <Flex minHeight="14px" minWidth="14px">
        {!isSaving && isSaved && <Icon name="checkmarkCircle" size={14} />}
        {isSaving && <Icon name="spinner" size={14} />}
      </Flex>
    </Flex>
  );
};

export default EditableField;
